import React, { useState } from "react";
import "./Payment.css";
import iconBox from "../assets/images/icon-box.svg";
import iconBoxCheck from "../assets/images/icon-box-check.svg";
import iconGooglePay from "../assets/images/icon-google.svg";
import iconPayPal from "../assets/images/icon-paypal.svg";
import iconApple from "../assets/images/icon-apple.svg";
import CreditCard from "../assets/images/credit-card.png";

const MEAL_PRICE = 14.95;
const DISCOUNT = 20;
const DELIVERY_FEE = 7.99;

const DAY_NAMES = [
  "Dimanche",
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
];
const MONTH_NAMES = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

function formatPrice(amount) {
  return amount.toFixed(2).replace(".", ",") + " $";
}

function formatDeliveryDate(date) {
  if (!date) return "Date à confirmer";
  return `${DAY_NAMES[date.getDay()]} ${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

const PAYMENT_METHODS = [
  { id: "card", label: "Carte de paiement", icon: null },
  { id: "paypal", label: "PayPal", icon: iconPayPal },
  { id: "apple", label: "Apple Pay", icon: iconApple },
  { id: "google", label: "Google Pay", icon: iconGooglePay },
];

export default function Payment({
  meals = [],
  mealQuantities = {},
  mealSelections = {},
  deliveryDate,
  onModifyDate,
  onModifyMeals,
}) {
  const [paymentMethod, setPaymentMethod] = useState("card");

  const addedMeals = meals.filter((m) => (mealQuantities[m.id] || 0) > 0);
  const mealsCount = Object.values(mealQuantities).reduce((s, q) => s + q, 0);

  const getGroups = (mealId) => {
    const selections = mealSelections[mealId] || [];
    if (!selections.length) {
      const qty = mealQuantities[mealId] || 0;
      return qty > 0 ? [{ side: "", size: "Régulier", count: qty }] : [];
    }
    const map = {};
    selections.forEach(({ side, size }) => {
      const key = `${size}||${side}`;
      if (!map[key]) map[key] = { side, size, count: 0 };
      map[key].count++;
    });
    return Object.values(map);
  };

  const regularRows = [];
  const familyRows = [];
  addedMeals.forEach((meal) => {
    getGroups(meal.id).forEach((group) => {
      if (group.size === "Familial") familyRows.push({ meal, group });
      else regularRows.push({ meal, group });
    });
  });
  const subtotal = mealsCount * MEAL_PRICE;
  const discountAmt = mealsCount > 0 ? DISCOUNT : 0;
  const total = Math.max(0, subtotal - discountAmt) + DELIVERY_FEE;

  return (
    <div className="payment">
      <h1 className="payment__title">Finalisez votre commande</h1>

      <div className="payment__layout">
        {/* ── LEFT COLUMN ── */}
        <div className="payment__left">
          {/* Profil */}
          <section className="payment__section">
            <h2 className="payment__section-title">Profil</h2>
            <div className="payment__row-2">
              <div className="payment__field">
                <label className="payment__label">Prénom</label>
                <input
                  className="payment__input"
                  type="text"
                  placeholder="John"
                />
              </div>
              <div className="payment__field">
                <label className="payment__label">Nom</label>
                <input
                  className="payment__input"
                  type="text"
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="payment__field">
              <label className="payment__label">Téléphone</label>
              <input
                className="payment__input"
                type="tel"
                placeholder="555 555-5555"
              />
            </div>
            <div className="payment__field">
              <label className="payment__label">Courriel</label>
              <input
                className="payment__input"
                type="email"
                placeholder="johndoe@wecookmeals.ca"
              />
            </div>
            <label className="payment__checkbox-row">
              <span className="payment__checkbox-box" />
              <input type="checkbox" className="payment__checkbox-input" />
              <span className="payment__checkbox-label">
                J'accepte de recevoir les menus de la semaine par texto et
                courriel
              </span>
            </label>
            <div className="payment__field">
              <input
                className="payment__input payment__input--disabled"
                type="password"
                placeholder="Créer le mot de passe"
                disabled
              />
            </div>
            <div className="payment__field">
              <input
                className="payment__input payment__input--disabled"
                type="password"
                placeholder="Confirmer le mot de passe"
                disabled
              />
            </div>
          </section>

          {/* Adresse de livraison */}
          <section className="payment__section">
            <h2 className="payment__section-title">Ajoutez votre adresse</h2>
            <div className="payment__field">
              <label className="payment__label">Adresse</label>
              <input
                className="payment__input"
                type="text"
                placeholder="1234 Fake street"
              />
            </div>
            <div className="payment__field">
              <label className="payment__label">Appartement (optionelle)</label>
              <input
                className="payment__input"
                type="text"
                placeholder="# ou N/A si non applicable"
              />
            </div>
            <div className="payment__row-2">
              <div className="payment__field">
                <label className="payment__label">Ville</label>
                <input
                  className="payment__input"
                  type="text"
                  placeholder="Montréal"
                />
              </div>
              <div className="payment__field">
                <label className="payment__label">Province</label>
                <input
                  className="payment__input"
                  type="text"
                  placeholder="Québec"
                />
              </div>
            </div>
            <div className="payment__row-2">
              <div className="payment__field">
                <label className="payment__label">Code postal</label>
                <input
                  className="payment__input"
                  type="text"
                  placeholder="J4Z 3T9"
                />
              </div>
              <div className="payment__field">
                <label className="payment__label">Numéro de téléphone</label>
                <input
                  className="payment__input"
                  type="tel"
                  placeholder="555-555-5555"
                />
              </div>
            </div>
          </section>

          {/* Adresse de facturation */}
          <section className="payment__section">
            <h2 className="payment__section-title">Adresse de facturation</h2>
            <label className="payment__radio-row">
              <span className="payment__radio payment__radio--checked" />
              <span className="payment__radio-label">
                Utiliser l'adresse de livraison comme adresse de facturation
              </span>
            </label>
            <div className="payment__field">
              <label className="payment__label">
                Spécifications de livraison
              </label>
              <input
                className="payment__input"
                type="text"
                placeholder="Adresse ligne 1"
              />
            </div>
          </section>

          {/* Paiement */}
          <section className="payment__section">
            <h2 className="payment__section-title payment__section-title--ruled">
              Paiement
            </h2>

            <div className="payment__methods">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  className={`payment__method${paymentMethod === m.id ? " payment__method--active" : ""}`}
                  onClick={() => setPaymentMethod(m.id)}
                  aria-pressed={paymentMethod === m.id}
                >
                  {m.icon ? (
                    <img
                      src={m.icon}
                      alt={m.label}
                      className="payment__method-icon"
                    />
                  ) : (
                    <span className="payment__method-text">{m.label}</span>
                  )}
                </button>
              ))}
            </div>

            {paymentMethod === "card" && (
              <div className="payment__card-fields">
                <div className="payment__field">
                  <label className="payment__label">Numéro de carte</label>
                  <input
                    className="payment__input payment__input--card"
                    type="text"
                    placeholder="1234 1234 1234 1234"
                  />
                  <img
                    src={CreditCard}
                    alt="Cartes acceptées"
                    className="payment__card-brands"
                  />
                </div>
                <div className="payment__row-2">
                  <div className="payment__field">
                    <label className="payment__label">Date d'expiration</label>
                    <input
                      className="payment__input payment__input--card"
                      type="text"
                      placeholder="MM/AA"
                    />
                  </div>
                  <div className="payment__field">
                    <label className="payment__label">Code CVC</label>
                    <input
                      className="payment__input payment__input--card"
                      type="text"
                      placeholder="CVC"
                    />
                  </div>
                </div>
              </div>
            )}

            <button className="payment__submit">Paiement &rarr;</button>
          </section>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="payment__right">
          {/* Delivery header */}
          <div className="payment__summary-header">
            <div className="payment__summary-delivery">
              <img
                src={iconBoxCheck}
                alt=""
                className="payment__summary-box-icon"
              />
              <div className="payment__summary-date-info">
                <span className="payment__summary-date-label">
                  Livraison du jeudi
                </span>
                <span className="payment__summary-date">
                  {formatDeliveryDate(deliveryDate)}
                </span>
              </div>
            </div>
            <button className="payment__summary-modify" onClick={onModifyDate}>
              Modifier
            </button>
          </div>

          {/* Repas section */}
          <div className="payment__summary-section">
            <div className="payment__summary-row">
              <p className="payment__summary-section-title">Repas</p>
              <button
                className="payment__summary-modify"
                onClick={onModifyMeals}
              >
                Modifier
              </button>
            </div>
            {addedMeals.length === 0 && (
              <p className="payment__summary-empty">Aucun repas sélectionné.</p>
            )}
            {regularRows.map(({ meal, group }) => (
              <div
                className="payment__summary-meal"
                key={`${meal.id}-${group.size}-${group.side}`}
              >
                <img
                  src={meal.image}
                  alt={meal.title}
                  className="payment__summary-meal-img"
                />
                <div className="payment__summary-meal-info">
                  <p className="payment__summary-meal-name">
                    {group.count > 1 && (
                      <span className="payment__summary-meal-qty">
                        {group.count}×&nbsp;
                      </span>
                    )}
                    {meal.title}
                  </p>
                  {group.side && (
                    <p className="payment__summary-meal-side">{group.side}</p>
                  )}
                </div>
                <p className="payment__summary-meal-price">
                  {formatPrice(MEAL_PRICE * group.count)}
                </p>
              </div>
            ))}
            {familyRows.length > 0 && (
              <p className="payment__summary-subsection-title">
                Format familial
              </p>
            )}
            {familyRows.map(({ meal, group }) => (
              <div
                className="payment__summary-meal"
                key={`${meal.id}-${group.size}-${group.side}`}
              >
                <img
                  src={meal.image}
                  alt={meal.title}
                  className="payment__summary-meal-img"
                />
                <div className="payment__summary-meal-info">
                  <p className="payment__summary-meal-name">
                    {group.count > 1 && (
                      <span className="payment__summary-meal-qty">
                        {group.count}×&nbsp;
                      </span>
                    )}
                    {meal.title}
                  </p>
                  {group.side && (
                    <p className="payment__summary-meal-side">{group.side}</p>
                  )}
                </div>
                <p className="payment__summary-meal-price">
                  {formatPrice(MEAL_PRICE * group.count)}
                </p>
              </div>
            ))}
          </div>

          {/* Promo / rabais */}
          <div className="payment__summary-section">
            <p className="payment__summary-section-title">
              Rabais première commande
            </p>
            <div className="payment__promo-row payment__promo-row--active">
              <div className="payment__promo-left">
                <span className="payment__promo-ordinal">
                  1<sup>ère</sup>
                </span>
                <img src={iconBoxCheck} alt="" className="payment__promo-box" />
              </div>
              <span className="payment__promo-label">20&nbsp;$ de rabais</span>
            </div>
            <div className="payment__promo-row">
              <div className="payment__promo-left">
                <span className="payment__promo-ordinal">
                  2<sup>ème</sup>
                </span>
                <img src={iconBox} alt="" className="payment__promo-box" />
              </div>
              <span className="payment__promo-label">20&nbsp;$ de rabais</span>
            </div>
            <div className="payment__promo-row">
              <div className="payment__promo-left">
                <span className="payment__promo-ordinal">
                  3<sup>ème</sup>
                </span>
                <img src={iconBox} alt="" className="payment__promo-box" />
              </div>
              <span className="payment__promo-label">20&nbsp;$ de rabais</span>
            </div>
          </div>

          {/* Invoice */}
          <div className="payment__invoice">
            <div className="payment__invoice-row">
              <span>Sous total</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="payment__invoice-row payment__invoice-row--discount">
              <span>Rabais</span>
              <span>-{formatPrice(discountAmt)}</span>
            </div>
            <div className="payment__invoice-row">
              <span>Livraison</span>
              <span>{formatPrice(DELIVERY_FEE)}</span>
            </div>
            <div className="payment__invoice-row">
              <span>TPS (5%)</span>
              <span>{formatPrice(0)}</span>
            </div>
            <div className="payment__invoice-row">
              <span>TVQ (9,975%)</span>
              <span>{formatPrice(0)}</span>
            </div>
            <div className="payment__invoice-total">
              <span>TOTAL</span>
              <span>{formatPrice(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
